import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useTranscript, { type TranscriptStatus } from '@hooks/useTranscript';

const Wrapper = styled.div<{ $disabled?: boolean }>`
  .ant-collapse {
    background-color: ${({ $disabled }) => ($disabled ? '#f0f0f0' : '#f9fafb')};
    border-color: #d1d5db;
  }

  .ant-collapse-header {
    align-items: center !important;
  }

  .ant-collapse-content {
    background-color: ${({ $disabled }) => ($disabled ? '#f0f0f0' : '#f9fafb')};
    border-top-color: #d1d5db;
  }
`;

const HeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const TranscriptContent = styled.div<{ $disabled?: boolean }>`
  background: ${({ $disabled }) => ($disabled ? '#f0f0f0' : 'white')};
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 16px;
  max-height: 384px;
  overflow-y: auto;
`;

const TranscriptText = styled.pre<{ $isEmpty?: boolean }>`
  white-space: pre-wrap;
  font-family: Helvetica, Arial, sans-serif;
  font-size: ${({ $isEmpty }) => ($isEmpty ? '12px' : '13px')};
  font-style: ${({ $isEmpty }) => ($isEmpty ? 'italic' : 'normal')};
  color: var(--black);
  opacity: ${({ $isEmpty }) => ($isEmpty ? 0.3 : 1)};
  line-height: 1.42;
  margin: 0;
`;

const LiveIndicator = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #d1d5db;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #1677ff;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

const statusLabelMap: Record<TranscriptStatus, string> = {
  live: 'Live',
  ready: 'Ready',
  not_live: 'Not live',
  processing: 'Processing',
  paused: 'Paused',
  review: 'Review'
};

const displayErrorText = {
  polling_transcript_error: 'Unable to load the latest transcript updates.',
  fetch_backup_transcript_error: 'Unable to load the transcript.',
  official_transcript_error:
    'Transcript may be incomplete, unavailable or not summarised due to a transcription error.',
  no_transcription_service_error: 'AI transcription was not used in this appointment'
};

const TranscriptAlert = ({ message }: { message: string }) => (
  <Alert type="warning" message={message} showIcon style={{ marginBottom: 12 }} />
);

const CollapsibleTranscript = () => {
  const { t } = useTranslation('common');
  const appointmentId = useSessionAppointmentId();
  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted }
  } = useSessionAppointment();
  const isDisabled = readOnly || isCompleted;
  const { transcriptText, isLoading, error, hasTranscriptGaps, status } = useTranscript(appointmentId);

  const [activeKey, setActiveKey] = useState<string[]>([]);
  const hasAutoExpanded = useRef(false);

  useEffect(() => {
    if (transcriptText && !hasAutoExpanded.current) {
      setActiveKey(['transcript']);
      hasAutoExpanded.current = true;
    }
  }, [transcriptText]);

  if (!appointmentId) return null;

  const isLive = status === 'live';
  const isProcessing = status === 'processing';

  const getDisplayText = () => {
    if (isLoading) return t('Loading transcript...');
    if (transcriptText === 'null') return t('Transcript will appear here as audio is processed.');
    return transcriptText;
  };
  const displayText = getDisplayText();

  const items = [
    {
      key: 'transcript',
      label: (
        <HeaderContent>
          <strong>{t('AI Transcript')}</strong>
          <StatusBadge>({t(statusLabelMap[status as TranscriptStatus])})</StatusBadge>
        </HeaderContent>
      ),
      children: (
        <TranscriptContent $disabled={isDisabled} tabIndex={0} aria-label={t('Transcript content')}>
          {hasTranscriptGaps && <TranscriptAlert message={t('Transcript may have gaps due to recording issues.')} />}
          {error && <TranscriptAlert message={t(displayErrorText[error])} />}
          <TranscriptText $isEmpty={!isLoading && transcriptText === 'null'}>{displayText}</TranscriptText>
          {(isLive || isProcessing) && (
            <LiveIndicator>
              <PulsingDot />
              <span>{isLive ? t('Transcribing…') : t('Processing transcript…')}</span>
            </LiveIndicator>
          )}
        </TranscriptContent>
      )
    }
  ];

  return (
    <Wrapper $disabled={isDisabled}>
      <Collapse activeKey={activeKey} onChange={(keys) => setActiveKey(keys)} items={items} bordered={false} />
    </Wrapper>
  );
};

export default CollapsibleTranscript;
